import React, { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import OpcHeader from "../_shared/OpcHeader/OpcHeader";
import { OpcBar, OpcCard, OpcRow } from "../_shared/OpcCard/OpcCard";
import {
  OpcPageError,
  OpcPageLoading,
} from "../_shared/OpcPageState/OpcPageState";
import okrPlansCoachStore, {
  useOkrPlansCoachStore,
} from "../_store/okrPlansCoachStore";
import useCoachPlan from "../_hooks/useCoachPlan";
import { REMINDABLE } from "../_config/okrPlansCoachConfig";
import {
  day,
  dayRange,
  firstNameOf,
  plural,
  reportName,
} from "../_config/okrPlansCoachFormat";
import { notifyError } from "../_config/okrPlansCoachNotify";
import {
  chatPath,
  companyPath,
  dashboardPath,
  reportPath,
  reviewPath,
} from "../_config/okrPlansCoachRoutes";
import PlanHead from "./PlanHead/PlanHead";
import NextStep from "./NextStep/NextStep";
import OpcShowMore, { OPC_PREVIEW } from "../_shared/OpcShowMore/OpcShowMore";
import ChangeLog from "./ChangeLog/ChangeLog";
import "../../_shared/ProjectStyles/ProjectsList.scss";

// ═══════════════════════════════════════════════════════════════════════════
// One OKR plan (coach) — BE 2.7 / 2.9; CD7, CD17, CD18, CD24. Header with
// fact tiles · one next step (Start Review / Remind / Complete Check in) ·
// left: Plan Review, Weekly AI Reports, Chat Threads, Change log (CD32) ·
// right: Key results & initiatives (KR / initiative %, "+N% this week"), Plan Documents.
// ═══════════════════════════════════════════════════════════════════════════

const selectLastCompany = (s) => s.lastCompany;

const OkrPlansCoachPlan = () => {
  const { planUniqueId: planId } = useParams();
  const history = useHistory();
  const { plan, error, reload } = useCoachPlan(planId);
  const lastCompany = useOkrPlansCoachStore(selectLastCompany);
  const selectLog = useCallback((s) => s.changeLogs[planId], [planId]);
  const log = useOkrPlansCoachStore(selectLog);
  const selectLogBusy = useCallback(
    (s) => !!s.pending[`changes:${planId}`],
    [planId],
  );
  const logBusy = useOkrPlansCoachStore(selectLogBusy);
  const selectRemindBusy = useCallback(
    (s) => !!s.pending[`remind:${planId}`],
    [planId],
  );
  const remindBusy = useOkrPlansCoachStore(selectRemindBusy);
  const [allReports, setAllReports] = useState(false);
  const [allChats, setAllChats] = useState(false);
  const [reminded, setReminded] = useState(false);

  const toCompany = () =>
    lastCompany
      ? history.push(companyPath(lastCompany.id, lastCompany.quarterCompanyId))
      : history.push(dashboardPath());
  const crumbs = (title) => [
    { title: "Dashboard", go: () => history.push(dashboardPath()) },
    ...(lastCompany ? [{ title: lastCompany.name, go: toCompany }] : []),
    { title },
  ];

  if (error)
    return (
      <div className="opc-screen">
        <OpcHeader crumbs={crumbs("OKR plan")} />
        <OpcPageError error={error} onRetry={reload} onBack={toCompany} />
      </div>
    );
  if (!plan)
    return (
      <div className="opc-screen">
        <OpcHeader crumbs={crumbs("OKR plan")} />
        <OpcPageLoading kind="plan" />
      </div>
    );

  const api = okrPlansCoachStore.getState();
  const owner = firstNameOf(plan.owner?.name) || "the owner";
  const review = plan.review || {};
  const closed =
    plan.is_archived ||
    plan.quarter?.is_read_only ||
    ["REVIEWED", "NOT_REVIEWED"].includes(review.state);
  const reports = (plan.weekly_reports || [])
    .slice()
    .sort((a, b) => (b.report_number || 0) - (a.report_number || 0));
  // CD43: collapsed, the reports list shows every report whose check-in is
  // due (all of them, however many); with none due, just the newest one.
  // "Show all N reports" opens the rest, in the same order.
  const isDue = (r) => r.remark?.state === "PENDING" && !closed;
  const dueReports = reports.filter(isDue);
  const collapsedReports = dueReports.length ? dueReports : reports.slice(0, 1);
  const shownReports = allReports ? reports : collapsedReports;
  const chats = plan.sme_chats || [];
  const files = plan.files || [];
  const krs = (plan.key_results || [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence);
  const inis = (plan.initiatives || [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence);

  const remind = () =>
    api
      .remind(planId)
      .then(() => setReminded(true))
      .catch(notifyError);

  // CD48: a submitted / finalised review lives only in the "Plan Review"
  // card (with the copy the top strip had) — never in both places.
  const reviewRow =
    review.state === "SUBMITTED"
      ? {
          text: `${owner} submitted the review on ${day(review.submitted_at)}.`,
          sub: "Finalise it to share the summary.",
          action: "Start review",
          primary: true,
        }
      : review.state === "REVIEWED"
        ? {
            text: `Review finalised on ${day(review.finalised_at)}.`,
            sub: `Submitted ${day(review.submitted_at)} · ${owner} can see the summary.`,
            action: "View summary",
          }
        : null;

  // The strip above the cards is only for "Remind {owner}" (CD7, CD20,
  // CD49); a submitted review and check-ins due are flagged on their cards.
  let next = null;
  if (!closed && REMINDABLE.includes(review.state)) {
    const text = {
      DUE_SOON: `${owner}'s review is due ${day(plan.review_date)}.`,
      DUE_TODAY: `${owner}'s final review is today.`,
      OVERDUE: `${owner}'s review is ${plural(review.days_late || 0, "day")} late.`,
      IN_PROGRESS: `${owner} has started the review.`,
    }[review.state];
    next = {
      text,
      sub: "You'll finalise it once it's submitted.",
      action: reminded
        ? "Reminded"
        : remindBusy
          ? "Sending…"
          : `Remind ${owner}`,
      onClick: remind,
      disabled: reminded || remindBusy,
    };
  }

  return (
    <div className="opc-screen">
      <OpcHeader crumbs={crumbs(plan.objective?.text)} />
      <div id="project-view" className="opc-pv">
        <PlanHead plan={plan} companyName={lastCompany?.name} />
        <NextStep step={next} />

        <div className="opc-pv-grid">
          <div className="opc-pv-col">
            {reviewRow ? (
              <OpcCard
                title="Plan Review"
                // CD49: teal edge while the review waits for the coach.
                className={reviewRow.primary ? "is-attention" : ""}
              >
                <OpcRow onClick={() => history.push(reviewPath(planId))}>
                  <span className="opc-pv-row__main">
                    <span className="dark-grey fw-bold small">
                      {reviewRow.text}
                    </span>
                    <span className="light-text small">{reviewRow.sub}</span>
                  </span>
                  <span className="opc-pv-status">
                    <span
                      className={
                        reviewRow.primary ? "text-primary-new" : "light-text"
                      }
                    >
                      {reviewRow.action}
                    </span>
                  </span>
                </OpcRow>
              </OpcCard>
            ) : null}

            <OpcCard
              title="Weekly AI Reports"
              count={reports.length}
              // CD49: teal edge while a check-in is due.
              className={dueReports.length ? "is-attention" : ""}
              tip="Generated every Monday from the owner's activity that week. They see it straight away; your check-in adds your remark to it."
            >
              {reports.length ? (
                <>
                  {shownReports.map((r) => (
                    <OpcRow
                      key={r.report_unique_id}
                      onClick={() =>
                        history.push(reportPath(planId, r.report_unique_id))
                      }
                    >
                      <span className="opc-pv-row__main">
                        <span className="small">
                          <span className="dark-grey fw-bold">
                            {reportName(r.report_number)}
                          </span>
                          <span className="light-text ms-2">
                            {dayRange(r.week_start, r.week_end)}
                          </span>
                          {r.kind === "QUIET" ? (
                            <span className="opc-tag ms-2">Quiet week</span>
                          ) : null}
                        </span>
                        <span className="light-text small opc-pv-row__text">
                          {r.headline}
                        </span>
                      </span>
                      <span className="opc-pv-status">
                        {isDue(r) ? (
                          <>
                            <i className="opc-pv-dot" />
                            <span className="dark-grey">Check-in due</span>
                          </>
                        ) : r.remark?.state === "ADDED" ? (
                          <>
                            <img src="/assets/images/tick-success.svg" alt="" />
                            <span className="light-text">Checked in</span>
                          </>
                        ) : null}
                      </span>
                    </OpcRow>
                  ))}
                  {/* CD43: due check-ins (or 1), then Show all ↔ Show less. */}
                  <OpcShowMore
                    total={reports.length}
                    shown={collapsedReports.length}
                    open={allReports}
                    onToggle={() => setAllReports((x) => !x)}
                    noun="reports"
                  />
                </>
              ) : (
                <p className="opc-pv-empty">
                  The first weekly report arrives on Monday. You&apos;ll add
                  your check-in to it here.
                </p>
              )}
            </OpcCard>

            <OpcCard
              title="Chat Threads"
              count={chats.length}
              tip="The owner's conversations with their AI coach. Read-only for you."
            >
              {chats.length ? (
                <>
                  {(allChats ? chats : chats.slice(0, OPC_PREVIEW)).map((c) => (
                    <OpcRow
                      key={c.chat_unique_id}
                      onClick={() =>
                        history.push(chatPath(planId, c.chat_unique_id))
                      }
                    >
                      <span className="opc-pv-row__main">
                        <span className="dark-grey small">
                          {c.name || "Untitled chat"}
                        </span>
                        <span className="light-text small">
                          {day(c.last_message_at || c.created_at)} ·{" "}
                          {plural(c.message_count || 0, "message")}
                        </span>
                      </span>
                    </OpcRow>
                  ))}
                  {/* CD42: 3 first, then Show all ↔ Show less. */}
                  <OpcShowMore
                    total={chats.length}
                    open={allChats}
                    onToggle={() => setAllChats((x) => !x)}
                    noun="chats"
                  />
                </>
              ) : (
                <p className="opc-pv-empty">
                  No conversation yet. You&apos;ll see {owner}&apos;s chats here
                  once they start one.
                </p>
              )}
            </OpcCard>
            {/* CD32 revised: compact, in the left column. */}
            <ChangeLog
              log={log}
              plan={plan}
              loading={logBusy}
              onMore={() => api.fetchOlderChanges(planId).catch(notifyError)}
            />
          </div>

          <div className="opc-pv-col">
            <OpcCard
              // CD41: the card holds key results and initiatives, not the
              // objective (that's the page title).
              title="Key results & initiatives"
              tip={`Updated by ${owner}. Progress is the average of the key results and shows in Quarterly OKRs too.`}
            >
              <div className="opc-pv-sub">Key results</div>
              {krs.map((k, i) => (
                <div className="opc-pv-kr" key={k.key_result_id}>
                  {/* CD51: a number badge, never "KR1". */}
                  <span className="opc-num">{i + 1}</span>
                  <span className="opc-pv-kr__text">
                    <span className="dark-grey small">{k.statement}</span>
                    {k.change_this_week ? (
                      <span className="light-text small">
                        {k.change_this_week > 0 ? "+" : ""}
                        {k.change_this_week}% this week
                      </span>
                    ) : null}
                  </span>
                  <OpcBar value={k.progress || 0} />
                </div>
              ))}
              {inis.length ? (
                <>
                  <div className="opc-pv-sub mt-3">Initiatives</div>
                  {inis.map((n, i) => (
                    <div className="opc-pv-kr" key={n.initiative_id}>
                      <span className="opc-num is-plain">{i + 1}</span>
                      <span className="opc-pv-kr__text">
                        <span className="dark-grey small">{n.statement}</span>
                        {n.change_this_week ? (
                          <span className="light-text small">
                            {n.change_this_week > 0 ? "+" : ""}
                            {n.change_this_week}% this week
                          </span>
                        ) : null}
                      </span>
                      <OpcBar value={n.progress || 0} />
                    </div>
                  ))}
                </>
              ) : null}
            </OpcCard>

            <OpcCard
              title="Plan Documents"
              count={files.length}
              tip="Files the owner added for their AI coach. View only."
            >
              {files.length ? (
                files.map((f) => (
                  <div className="opc-pv-file" key={f.rag_document_id}>
                    <img src="/assets/images/file-outline-primary.svg" alt="" />
                    <span className="opc-pv-row__main">
                      <span className="dark-grey small">{f.name}</span>
                      <span className="light-text small">
                        Uploaded {day(f.uploaded_at)}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="opc-pv-icon"
                      aria-label={`Download ${f.name}`}
                      onClick={() =>
                        api
                          .downloadFile(f.rag_document_id, f.name)
                          .catch(notifyError)
                      }
                    >
                      <img src="/assets/images/download-dark.svg" alt="" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="opc-pv-empty">No documents yet.</p>
              )}
            </OpcCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OkrPlansCoachPlan;
